import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, first, map, merge } from 'rxjs';
import { ApiState } from '../../../model/api-state.model';
import { ProcessType } from '../../../model/process-type.model';
import { FormsService } from '../../../services/forms.service';
import { ModalService } from '../../../services/modal.service';
import { AppState } from '../../../store';
import * as fromProcess from '../../../store/process/process.reducer';
import { PersonsWith } from '../../../model/offer';

const BACKEND_ID_DIFF = 1;

interface CustomizeForTheActiveIncludedParams {
  controls: FormControl<boolean | null>[];
  birthYears: number[];
  controlsID: number[];
}

@Component({
  selector: 'app-customize-for-the-active',
  templateUrl: './customize-for-the-active.component.html',
  styleUrls: ['./customize-for-the-active.component.scss'],
})
export class CustomizeForTheActiveComponent {
  readonly apiState = ApiState;
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  insureds$ = merge(
    this.store.select(fromProcess.selectQuotationInsureds),
    this.store.select(fromProcess.selectPremiumCalculationInsureds),
  ).pipe(filter((insureds) => !!insureds));

  form = new FormGroup({
    customizeFotTheActiveIncluded: new FormArray<FormControl<boolean | null>>([new FormControl(false)]),
  });
  customizeControlsID: number[] = [];
  customizeStatus$ = new BehaviorSubject<ApiState>(ApiState.idle);

  labelArgs$ = combineLatest([this.processKind$, this.insureds$]).pipe(
    first(),
    map(([kind, insureds]) => {
      let labelArgs: Record<string, string>[] = [];

      let travelDateFrom: string | null = null;
      let insuredBirthYear: (number | null)[] = [];
      switch (kind) {
        case ProcessType.TRAVELINPL: {
          travelDateFrom = this.formsService.travelInPl.controls.travelDates.controls.dateFrom.value;
          insuredBirthYear = this.formsService.travelInPl.controls.insuredBirthYear.value;
          break;
        }
        case ProcessType.TRIPABROAD: {
          travelDateFrom = this.formsService.tripAbroad.controls.travelDates.controls.dateFrom.value;
          insuredBirthYear = this.formsService.tripAbroad.controls.insuredBirthYear.value;
          break;
        }
        default: {
          throw new Error('Not implemented');
        }
      }

      if (insureds) {
        const { controls, birthYears, controlsID } = insureds.reduce<CustomizeForTheActiveIncludedParams>(
          (acc, insured) => {
            if (travelDateFrom) {
              if (insured.coverages.length) {
                const value = insured.coverages.some((coverage) => coverage.codeName === 'ForTheActive' && coverage.selected);

                acc.controls.push(new FormControl<boolean | null>(value));
                acc.birthYears.push(insured.birthYear);
                acc.controlsID.push(insured.id);
              } else {
                acc.controls.push(new FormControl<boolean | null>(null));
                acc.birthYears.push(insured.birthYear);
                acc.controlsID.push(insured.id);
              }
            }

            return acc;
          },
          { controls: [], birthYears: [], controlsID: [] },
        );

        this.form.setControl('customizeFotTheActiveIncluded', new FormArray(controls));
        labelArgs = birthYears.map((birthYear) => ({ year: String(birthYear || '----') })) || [];

        this.customizeControlsID = controlsID;
      } else {
        const { controls, birthYears, controlsID } = insuredBirthYear.reduce<CustomizeForTheActiveIncludedParams>(
          (acc, birthYear, id) => {
            if (birthYear && travelDateFrom) {
              acc.controls.push(new FormControl<boolean | null>(null));
              acc.birthYears.push(birthYear);
              acc.controlsID.push(id + BACKEND_ID_DIFF);
            }

            return acc;
          },
          { controls: [], birthYears: [], controlsID: [] },
        );
        this.form.setControl('customizeFotTheActiveIncluded', new FormArray(controls));
        labelArgs = birthYears.map((birthYear) => ({ year: String(birthYear || '----') })) || [];

        this.customizeControlsID = controlsID;
      }

      return labelArgs;
    }),
  );

  constructor(private store: Store<AppState>, private modalService: ModalService, private formsService: FormsService) {}

  handleSubmit() {
    const customizeForTheActiveIncluded: PersonsWith[] =
      this.form.value.customizeFotTheActiveIncluded?.map((selected, index) => ({
        id: this.customizeControlsID[index],
        selected: selected ?? false,
      })) || [];
    this.store.dispatch(fromProcess.actions.setCustomizeForTheActiveIncluded({ customizeForTheActiveIncluded }));
    this.store.dispatch(
      fromProcess.actions.selectPackageAddons({
        addon: 'ForTheActive',
        value: customizeForTheActiveIncluded?.some(({ selected }) => selected),
      }),
    );
    this.modalService.closeModalAll();
  }
}
